<script setup>
import { computed, onMounted, onUnmounted, ref, watch, watchEffect } from 'vue'
import { company, getAuthToken, API_BASE_URL, isDevUser } from '@/stores/appConfig'
import { logAction } from '@/utils/logAction'
import LeadsKanbanBoard from '@/components/leads/LeadsKanbanBoard.vue'
import ContactFormModal from '@/components/leads/ContactFormModal.vue'
import LeadFormModal from '@/components/leads/LeadFormModal.vue'
import JobFormModal from '@/components/jobs/JobFormModal.vue'
import { statusLabels, statusIcons, sourceLabels } from '@/constants/leads'

const filters = [
  { label: 'Inbox', value: 'all', icon: 'mdi-inbox-outline' },
  { label: 'New', value: 'new', icon: 'mdi-email-outline' },
  { label: 'Contacted', value: 'contacted', icon: 'mdi-phone-outline' },
  { label: 'Quoted', value: 'quoted', icon: 'mdi-file-document-edit-outline' },
  { label: 'Won', value: 'won', icon: 'mdi-check-circle-outline' },
  { label: 'Lost', value: 'lost', icon: 'mdi-close-circle-outline' },
]

const statusOptions = filters.filter((filter) => filter.value !== 'all')

const channelLabels = {
  email: 'Email',
  sms: 'Text',
}

const channelIcons = {
  email: 'mdi-email-outline',
  sms: 'mdi-message-text-outline',
}

const leads = ref([])
const contacts = ref([])
const loading = ref(false)
const loadError = ref('')

const selectedLeadId = ref(null)
const selectedLead = ref(null)
const detailLoading = ref(false)
const detailError = ref('')

const activeFilter = ref('all')
const searchTerm = ref('')
const viewMode = ref('list')
const noteDraft = ref('')
const noteSending = ref(false)
const statusUpdating = ref(false)

const showContactForm = ref(false)

const showLeadForm = ref(false)

const showJobForm = ref(false)
const jobPrefill = ref(null)

const showMessageModal = ref(false)
const messageMode = ref('email')
const messageDraft = ref({ subject: '', body: '' })
const messageSending = ref(false)
const messageError = ref('')

const reminderDraft = ref({ due_at: '', note: '' })
const reminderSending = ref(false)
const reminderError = ref('')

async function authHeaders() {
  const token = await getAuthToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function leadSubject(lead) {
  if (!lead) return ''
  return lead.service || (lead.message ? lead.message.slice(0, 60) : '') || 'Website inquiry'
}

function formatRelativeTime(dateString) {
  if (!dateString) return ''

  const date = new Date(dateString)
  const diffMs = Date.now() - date.getTime()
  const diffMin = Math.round(diffMs / 60000)

  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m`

  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h`

  const diffDay = Math.round(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d`

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function formatFullTime(dateString) {
  if (!dateString) return ''

  return new Date(dateString).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function messageIsOutbound(message) {
  return message.direction === 'outbound'
}

function messageAuthor(message) {
  if (messageIsOutbound(message)) {
    return company.company_name || 'You'
  }

  return selectedLead.value?.contact_name || 'Customer'
}

function isListReminderOverdue(lead) {
  return !!lead.next_reminder_due_at && new Date(lead.next_reminder_due_at).getTime() < Date.now()
}

function isOverdue(reminder) {
  return !reminder.completed_at && new Date(reminder.due_at).getTime() < Date.now()
}

const newQuoteRoute = computed(() => {
  if (!selectedLead.value) return { path: '/quote' }

  return {
    path: '/quote',
    query: {
      contact_id: selectedLead.value.contact_id,
      lead_id: selectedLead.value.id,
      company_id: company.company_id,
      customer_name: selectedLead.value.contact_name || '',
      customer_email: selectedLead.value.email || '',
      customer_phone: selectedLead.value.phone || '',
    },
  }
})

function leadMatchesSearch(lead, search) {
  if (!search) return true

  return [lead.contact_name, lead.email, lead.phone, lead.service, lead.message, lead.source]
    .join(' ')
    .toLowerCase()
    .includes(search)
}

const filteredLeads = computed(() => {
  const search = searchTerm.value.trim().toLowerCase()

  return leads.value.filter((lead) => {
    const matchesFilter =
      activeFilter.value === 'all' || lead.status === activeFilter.value

    return matchesFilter && leadMatchesSearch(lead, search)
  })
})

const boardLeads = computed(() => {
  const search = searchTerm.value.trim().toLowerCase()
  return leads.value.filter((lead) => leadMatchesSearch(lead, search))
})

const inboxStats = computed(() => {
  const openLeads = leads.value.filter((lead) => !['won', 'lost'].includes(lead.status))
  const newLeads = leads.value.filter((lead) => lead.status === 'new')
  const needsReply = leads.value.filter((lead) => lead.last_message_direction === 'inbound')

  return [
    {
      label: 'Open',
      value: openLeads.length,
      icon: 'mdi-inbox-outline',
    },
    {
      label: 'New',
      value: newLeads.length,
      icon: 'mdi-email-outline',
    },
    {
      label: 'Needs reply',
      value: needsReply.length,
      icon: 'mdi-reply-outline',
    },
  ]
})

async function fetchLeads() {
  if (!company.company_id) return

  loading.value = true
  loadError.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/leads?company_id=${encodeURIComponent(company.company_id)}`,
      { headers }
    )
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to load leads.')
    }

    leads.value = data.leads || []

    if (!selectedLeadId.value && leads.value.length) {
      selectLead(leads.value[0].id)
    }
  } catch (err) {
    loadError.value = err.message || 'Failed to load leads.'
  } finally {
    loading.value = false
  }
}

async function fetchContacts() {
  if (!company.company_id) return

  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/contacts?company_id=${encodeURIComponent(company.company_id)}`,
      { headers }
    )
    const data = await response.json().catch(() => ({}))

    if (response.ok && data.ok) contacts.value = data.contacts || []
  } catch {
    contacts.value = []
  }
}

function mapLeadDetail(data) {
  return {
    ...data.lead,
    contact: data.contact || null,
    messages: data.messages || [],
    internalNotes: data.notes || [],
    quotes: data.quotes || [],
    jobs: data.jobs || [],
    reminders: data.reminders || [],
    statusHistory: data.statusHistory || [],
    timeline: data.timeline || [],
  }
}

async function selectLead(id) {
  selectedLeadId.value = id
  detailLoading.value = true
  detailError.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/leads/${id}`, { headers })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to load lead.')
    }

    selectedLead.value = mapLeadDetail(data)
  } catch (err) {
    detailError.value = err.message || 'Failed to load lead.'
  } finally {
    detailLoading.value = false
  }
}

// Background refresh so inbound activity that lands while a lead is open
// (an emailed reply arriving via the inbound-email webhook, a status change
// from another teammate, etc.) shows up without the contractor needing to
// reselect the lead or reload the page. Silent — selectLead() above already
// surfaces load failures; a missed poll just gets picked up by the next one.
const ACTIVITY_POLL_INTERVAL_MS = 15000
let activityPollTimer = null

async function pollSelectedLead() {
  if (!selectedLeadId.value || document.hidden) return

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/leads/${selectedLeadId.value}`, { headers })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) return
    if (selectedLeadId.value !== data.lead?.id) return

    selectedLead.value = mapLeadDetail(data)
  } catch {
    // ignore — this is a quiet background refresh
  }
}

onMounted(() => {
  activityPollTimer = setInterval(pollSelectedLead, ACTIVITY_POLL_INTERVAL_MS)
})

onUnmounted(() => {
  if (activityPollTimer) clearInterval(activityPollTimer)
})

function setFilter(value) {
  activeFilter.value = value

  const firstMatch = filteredLeads.value[0]

  if (firstMatch) {
    selectLead(firstMatch.id)
  } else {
    selectedLeadId.value = null
    selectedLead.value = null
  }
}

watch(filteredLeads, (matches) => {
  if (!matches.length) {
    selectedLeadId.value = null
    selectedLead.value = null
    return
  }

  const selectedIsVisible = matches.some(
    (lead) => lead.id === selectedLeadId.value
  )

  if (!selectedIsVisible) {
    selectLead(matches[0].id)
  }
})

watchEffect(() => {
  if (company.company_id) {
    fetchLeads()
    fetchContacts()
  }
})

async function changeLeadStatus(leadId, status) {
  if (statusUpdating.value) return

  statusUpdating.value = true

  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/leads/${leadId}/status`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ status }),
      }
    )
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to update status.')
    }

    const row = leads.value.find((item) => item.id === leadId)
    const previousStatus = row?.status
    if (row) row.status = data.lead.status

    if (selectedLead.value && selectedLead.value.id === leadId && previousStatus !== data.lead.status) {
      selectedLead.value.status = data.lead.status
      selectedLead.value.timeline = [
        ...selectedLead.value.timeline,
        {
          type: 'status_change',
          id: `local-${Date.now()}`,
          from_status: previousStatus,
          to_status: data.lead.status,
          created_at: new Date().toISOString(),
        },
      ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    }

    const token = await getAuthToken()
    logAction(token, {
      action: 'lead.status-change',
      label: `Marked lead as ${statusLabels[data.lead.status] || data.lead.status}`,
      company_id: company.company_id,
      metadata: { lead_id: leadId, from: previousStatus, to: data.lead.status },
    })
  } catch (err) {
    detailError.value = err.message || 'Failed to update status.'
  } finally {
    statusUpdating.value = false
  }
}

async function updateStatus(status) {
  if (!selectedLead.value) return
  await changeLeadStatus(selectedLead.value.id, status)
}

function onKanbanStatusChange({ lead, newStatus }) {
  changeLeadStatus(lead.id, newStatus)
}

function onKanbanSelect(id) {
  viewMode.value = 'list'
  selectLead(id)
}

function openMessageModal(mode) {
  messageMode.value = mode
  messageDraft.value = { subject: '', body: '' }
  messageError.value = ''
  showMessageModal.value = true
}

function closeMessageModal() {
  showMessageModal.value = false
  messageDraft.value = { subject: '', body: '' }
  messageError.value = ''
}

async function sendMessage() {
  const body = messageDraft.value.body.trim()

  if (!selectedLead.value || !body || messageSending.value || messageMode.value !== 'email') return

  messageSending.value = true
  messageError.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/leads/${selectedLead.value.id}/reply`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ body, subject: messageDraft.value.subject.trim() || undefined }),
      }
    )
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to send email.')
    }

    selectedLead.value.messages.push(data.message)
    selectedLead.value.timeline = [...selectedLead.value.timeline, { ...data.message, type: 'message' }].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    )
    selectedLead.value.status = data.lead.status

    const row = leads.value.find((item) => item.id === selectedLead.value.id)
    if (row) row.status = data.lead.status

    closeMessageModal()

    const token = await getAuthToken()
    logAction(token, {
      action: 'lead.reply',
      label: 'Emailed lead',
      company_id: company.company_id,
      metadata: { lead_id: selectedLead.value.id },
    })
  } catch (err) {
    messageError.value = err.message || 'Failed to send email.'
  } finally {
    messageSending.value = false
  }
}

async function addNote() {
  const note = noteDraft.value.trim()

  if (!selectedLead.value || !note || noteSending.value) return

  noteSending.value = true

  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/leads/${selectedLead.value.id}/notes`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ body: note }),
      }
    )
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to add note.')
    }

    selectedLead.value.internalNotes.unshift(data.note)
    selectedLead.value.timeline = [...selectedLead.value.timeline, { ...data.note, type: 'note' }].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    )
    noteDraft.value = ''

    const token = await getAuthToken()
    logAction(token, {
      action: 'lead.note-add',
      label: 'Added a note to lead',
      company_id: company.company_id,
      metadata: { lead_id: selectedLead.value.id },
    })
  } catch (err) {
    detailError.value = err.message || 'Failed to add note.'
  } finally {
    noteSending.value = false
  }
}

function openCreateLead() {
  showLeadForm.value = true
}

function onLeadCreated(lead) {
  leads.value.unshift(lead)
  fetchContacts()
  selectLead(lead.id)
}

function jobForQuote(quote) {
  return (selectedLead.value?.jobs || []).find((job) => job.quote_id === quote.id)
}

function openScheduleJob(quote) {
  if (!selectedLead.value) return

  jobPrefill.value = {
    quote_id: quote.id,
    contact_id: selectedLead.value.contact_id,
    lead_id: selectedLead.value.id,
    title: quote.title || `Job for ${selectedLead.value.contact_name}`,
    address: quote.job_address || selectedLead.value.address || '',
  }
  showJobForm.value = true
}

function onJobScheduled() {
  jobPrefill.value = null
  if (selectedLead.value) selectLead(selectedLead.value.id)
}

function openEditContact() {
  if (!selectedLead.value) return
  showContactForm.value = true
}

async function deleteLead(lead) {
  if (!lead) return
  if (!confirm(`Delete this lead for ${lead.contact_name}? This won't delete the contact or its message history.`)) return

  detailError.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(`${API_BASE_URL}/leads/${lead.id}`, {
      method: 'DELETE',
      headers,
    })
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to delete lead.')
    }

    leads.value = leads.value.filter((item) => item.id !== lead.id)

    const token = await getAuthToken()
    logAction(token, {
      action: 'lead.delete',
      label: `Deleted lead for ${lead.contact_name}`,
      company_id: company.company_id,
      metadata: { lead_id: lead.id },
    })
  } catch (err) {
    detailError.value = err.message || 'Failed to delete lead.'
  }
}

function onContactSaved(contact) {
  // A contact edit can affect multiple visible lead rows — patch every lead
  // sharing this contact_id, not just the one currently selected.
  for (const row of leads.value) {
    if (row.contact_id === contact.id) Object.assign(row, contact)
  }

  if (selectedLead.value && selectedLead.value.contact_id === contact.id) {
    selectedLead.value = { ...selectedLead.value, ...contact, contact }
  }
}

async function addReminder() {
  if (!selectedLead.value || !reminderDraft.value.due_at || reminderSending.value) return

  reminderSending.value = true
  reminderError.value = ''

  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/leads/${selectedLead.value.id}/reminders`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          due_at: reminderDraft.value.due_at,
          note: reminderDraft.value.note.trim() || undefined,
        }),
      }
    )
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to add reminder.')
    }

    selectedLead.value.reminders.push(data.reminder)
    selectedLead.value.reminders.sort((a, b) => new Date(a.due_at) - new Date(b.due_at))
    reminderDraft.value = { due_at: '', note: '' }

    const token = await getAuthToken()
    logAction(token, {
      action: 'lead.reminder-add',
      label: 'Set a reminder',
      company_id: company.company_id,
      metadata: { lead_id: selectedLead.value.id },
    })
  } catch (err) {
    reminderError.value = err.message || 'Failed to add reminder.'
  } finally {
    reminderSending.value = false
  }
}

async function toggleReminder(reminder) {
  if (!selectedLead.value) return

  const completing = !reminder.completed_at

  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/leads/${selectedLead.value.id}/reminders/${reminder.id}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ completed: completing }),
      }
    )
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to update reminder.')
    }

    const target = selectedLead.value.reminders.find((item) => item.id === reminder.id)
    if (target) target.completed_at = data.reminder.completed_at

    if (completing) {
      const token = await getAuthToken()
      logAction(token, {
        action: 'lead.reminder-complete',
        label: 'Completed a reminder',
        company_id: company.company_id,
        metadata: { lead_id: selectedLead.value.id, reminder_id: reminder.id },
      })
    }
  } catch (err) {
    reminderError.value = err.message || 'Failed to update reminder.'
  }
}

async function deleteReminder(reminder) {
  if (!selectedLead.value) return

  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/leads/${selectedLead.value.id}/reminders/${reminder.id}`,
      { method: 'DELETE', headers }
    )
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to delete reminder.')
    }

    selectedLead.value.reminders = selectedLead.value.reminders.filter(
      (item) => item.id !== reminder.id
    )
  } catch (err) {
    reminderError.value = err.message || 'Failed to delete reminder.'
  }
}
</script>

<template>
  <main class="leads-page">
    <section class="leads-shell">
      <header class="leads-header">
        <div>
          <p class="leads-eyebrow">Website inbox</p>
          <h1>Leads</h1>
        </div>

        <div class="header-right">
          <div class="stats-strip" aria-label="Lead stats">
            <div
              v-for="stat in inboxStats"
              :key="stat.label"
              class="stat-item"
            >
              <v-icon size="20">{{ stat.icon }}</v-icon>
              <strong>{{ stat.value }}</strong>
              <span>{{ stat.label }}</span>
            </div>
          </div>

          <div class="view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              class="view-toggle-button"
              :class="{ active: viewMode === 'list' }"
              @click="viewMode = 'list'"
            >
              <v-icon size="17">mdi-view-list-outline</v-icon>
              <span>List</span>
            </button>
            <button
              type="button"
              class="view-toggle-button"
              :class="{ active: viewMode === 'board' }"
              @click="viewMode = 'board'"
            >
              <v-icon size="17">mdi-view-column-outline</v-icon>
              <span>Board</span>
            </button>
          </div>

          <button class="new-contact-button" type="button" @click="openCreateLead">
            <v-icon size="18">mdi-account-plus-outline</v-icon>
            <span>New Lead</span>
          </button>
        </div>
      </header>

      <div v-if="loadError" class="banner banner-error">{{ loadError }}</div>

      <div v-if="viewMode === 'board'" class="board-layout">
        <LeadsKanbanBoard
          :leads="boardLeads"
          :statuses="statusOptions"
          @status-change="onKanbanStatusChange"
          @select="onKanbanSelect"
        />
      </div>

      <div v-else class="inbox-layout">
        <aside class="inbox-panel">
          <div class="inbox-toolbar">
            <label class="search-field">
              <v-icon size="20">mdi-magnify</v-icon>
              <input
                v-model="searchTerm"
                type="search"
                placeholder="Search leads"
              />
            </label>
          </div>

          <div class="filter-row" aria-label="Lead filters">
            <button
              v-for="filter in filters"
              :key="filter.value"
              class="filter-button"
              :class="{ active: activeFilter === filter.value }"
              type="button"
              @click="setFilter(filter.value)"
            >
              <v-icon size="18">{{ filter.icon }}</v-icon>
              <span>{{ filter.label }}</span>
            </button>
          </div>

          <div class="lead-list" aria-label="Lead list">
            <div v-if="loading" class="empty-list">
              <v-progress-circular indeterminate size="24" color="primary" />
              <span>Loading leads…</span>
            </div>

            <template v-else>
              <button
                v-for="lead in filteredLeads"
                :key="lead.id"
                class="lead-row"
                :class="{ selected: lead.id === selectedLeadId }"
                type="button"
                @click="selectLead(lead.id)"
              >
                <span class="lead-avatar">
                  {{ (lead.contact_name || '?').charAt(0) }}
                </span>

                <span class="lead-summary">
                  <span class="lead-row-top">
                    <strong>{{ lead.contact_name }}</strong>
                    <span class="row-time">
                      <v-icon
                        v-if="isListReminderOverdue(lead)"
                        size="14"
                        color="#c0392b"
                        title="Reminder overdue"
                      >mdi-alarm</v-icon>
                      {{ formatRelativeTime(lead.created_at) }}
                    </span>
                  </span>
                  <span class="lead-subject">{{ leadSubject(lead) }}</span>
                  <span class="lead-meta">
                    <span>{{ lead.service || 'General inquiry' }}</span>
                    <span class="dot"></span>
                    <span>{{ sourceLabels[lead.source] || lead.source }}</span>
                    <template v-if="lead.is_repeat_customer">
                      <span class="dot"></span>
                      <span class="repeat-tag">Returning</span>
                    </template>
                  </span>
                </span>

                <span
                  class="status-pill"
                  :class="`status-${lead.status}`"
                >
                  {{ statusLabels[lead.status] }}
                </span>
              </button>

              <div v-if="!filteredLeads.length" class="empty-list">
                <v-icon size="28">mdi-inbox-remove-outline</v-icon>
                <span>No leads found</span>
              </div>
            </template>
          </div>
        </aside>

        <section
          v-if="selectedLead"
          class="detail-panel"
          aria-label="Lead details"
        >
          <div v-if="detailError" class="banner banner-error">{{ detailError }}</div>

          <div class="detail-header">
            <div class="detail-title">
              <span class="detail-avatar">
                {{ (selectedLead.contact_name || '?').charAt(0) }}
              </span>
              <div>
                <div class="detail-name-row">
                  <h2>{{ selectedLead.contact_name }}</h2>
                  <span v-if="selectedLead.is_repeat_customer" class="status-pill repeat-pill">
                    <v-icon size="14">mdi-star</v-icon>
                    Returning Customer
                  </span>
                  <span
                    class="status-pill"
                    :class="`status-${selectedLead.status}`"
                  >
                    <v-icon size="15">
                      {{ statusIcons[selectedLead.status] }}
                    </v-icon>
                    {{ statusLabels[selectedLead.status] }}
                  </span>
                </div>
                <p>{{ leadSubject(selectedLead) }}</p>
              </div>
            </div>

            <div class="detail-actions" aria-label="Lead actions">
              <RouterLink
                class="channel-button"
                :to="`/crm/customers/${selectedLead.contact_id}`"
                title="View profile"
              >
                <v-icon size="17">mdi-account-details-outline</v-icon>
                <span>Profile</span>
              </RouterLink>
              <button
                class="channel-button"
                type="button"
                title="Edit contact"
                @click="openEditContact"
              >
                <v-icon size="17">mdi-pencil-outline</v-icon>
                <span>Edit</span>
              </button>
              <button
                class="channel-button danger"
                type="button"
                title="Delete contact"
                @click="deleteLead(selectedLead)"
              >
                <v-icon size="17">mdi-trash-can-outline</v-icon>
                <span>Delete</span>
              </button>
              <button
                class="channel-button"
                type="button"
                title="Send email"
                @click="openMessageModal('email')"
              >
                <v-icon size="17">mdi-email-outline</v-icon>
                <span>Email</span>
              </button>
              <button
                class="channel-button"
                type="button"
                title="Send text"
                @click="openMessageModal('text')"
              >
                <v-icon size="17">mdi-message-text-outline</v-icon>
                <span>Text</span>
              </button>
              <v-select
                :model-value="selectedLead.status"
                :items="statusOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                density="compact"
                hide-details
                :disabled="statusUpdating"
                style="min-width: 168px"
                @update:model-value="updateStatus"
              />
            </div>
          </div>

          <div class="detail-grid">
            <div class="field-group">
              <span>Email</span>
              <strong>{{ selectedLead.email }}</strong>
            </div>
            <div class="field-group">
              <span>Phone</span>
              <strong>{{ selectedLead.phone || '—' }}</strong>
            </div>
            <div class="field-group">
              <span>Service</span>
              <strong>{{ selectedLead.service || '—' }}</strong>
            </div>
            <div class="field-group">
              <span>Source</span>
              <strong>{{ sourceLabels[selectedLead.source] || selectedLead.source || '—' }}</strong>
            </div>
            <div class="field-group">
              <span>Address</span>
              <strong>{{ selectedLead.address || '—' }}</strong>
            </div>
            <div class="field-group">
              <span>Received</span>
              <strong>{{ formatFullTime(selectedLead.created_at) }}</strong>
            </div>
          </div>

          <div class="detail-body">
            <section class="conversation-panel">
              <div class="section-header">
                <h3>Activity</h3>
                <span>{{ selectedLead.service }}</span>
              </div>

              <div class="message-thread">
                <template v-for="entry in selectedLead.timeline" :key="`${entry.type}-${entry.id}`">
                  <article
                    v-if="entry.type === 'message'"
                    class="message-bubble"
                    :class="{ contractor: messageIsOutbound(entry) }"
                  >
                    <div class="message-meta">
                      <span class="message-author-group">
                        <strong>{{ messageAuthor(entry) }}</strong>
                        <span class="channel-badge" :class="`channel-${entry.channel || 'email'}`">
                          <v-icon size="12">{{ channelIcons[entry.channel] || channelIcons.email }}</v-icon>
                          {{ channelLabels[entry.channel] || channelLabels.email }}
                        </span>
                      </span>
                      <span>{{ formatFullTime(entry.created_at) }}</span>
                    </div>
                    <p v-if="entry.subject" class="message-subject">{{ entry.subject }}</p>
                    <p class="message-body">{{ entry.body }}</p>
                  </article>

                  <div v-else-if="entry.type === 'note'" class="note-item timeline-note">
                    <span class="timeline-note-label">
                      <v-icon size="12">mdi-note-text-outline</v-icon>
                      Internal note
                    </span>
                    <p class="message-body">{{ entry.body }}</p>
                    <span class="timeline-status-time">{{ formatFullTime(entry.created_at) }}</span>
                  </div>

                  <div v-else-if="entry.type === 'status_change'" class="timeline-status">
                    <v-icon size="15">{{ statusIcons[entry.to_status] }}</v-icon>
                    <span>Marked as <strong>{{ statusLabels[entry.to_status] || entry.to_status }}</strong></span>
                    <span class="timeline-status-time">{{ formatFullTime(entry.created_at) }}</span>
                  </div>

                  <div v-else-if="entry.type === 'quote'" class="timeline-status timeline-quote">
                    <v-icon size="15">mdi-file-document-outline</v-icon>
                    <span>Quote <strong>{{ entry.quote_number }}</strong> — {{ entry.status }}</span>
                    <span class="timeline-status-time">{{ formatFullTime(entry.created_at) }}</span>
                  </div>

                  <div v-else-if="entry.type === 'job'" class="timeline-status timeline-job">
                    <v-icon size="15">mdi-briefcase-outline</v-icon>
                    <span>Job <strong>{{ entry.title }}</strong> — scheduled for {{ formatFullTime(entry.scheduled_start) }}</span>
                    <span class="timeline-status-time">{{ formatFullTime(entry.created_at) }}</span>
                  </div>
                </template>

                <p v-if="!selectedLead.timeline.length" class="muted-note">
                  No activity yet
                </p>
              </div>

              <div class="composer-row">
                <div class="note-box">
                  <textarea
                    v-model="noteDraft"
                    rows="3"
                    placeholder="Add an internal note… (Ctrl+Enter to add)"
                    @keydown.ctrl.enter.prevent="addNote"
                  ></textarea>
                  <button
                    class="note-button"
                    type="button"
                    :disabled="!noteDraft.trim() || noteSending"
                    title="Add note"
                    @click="addNote"
                  >
                    <v-icon size="19">mdi-plus</v-icon>
                  </button>
                </div>
              </div>
            </section>

            <aside class="side-panel">
              <section class="form-panel">
                <div class="section-header">
                  <h3>Form submission</h3>
                </div>

                <dl class="submission-list">
                  <div>
                    <dt>Name</dt>
                    <dd>{{ selectedLead.contact_name }}</dd>
                  </div>
                  <div>
                    <dt>Service</dt>
                    <dd>{{ selectedLead.service || '—' }}</dd>
                  </div>
                  <div>
                    <dt>Source</dt>
                    <dd>{{ sourceLabels[selectedLead.source] || selectedLead.source || '—' }}</dd>
                  </div>
                  <div>
                    <dt>Message</dt>
                    <dd>{{ selectedLead.message || '—' }}</dd>
                  </div>
                </dl>
              </section>

              <section class="quotes-panel">
                <div class="section-header">
                  <h3>Quotes</h3>
                  <RouterLink class="new-quote-link" :to="newQuoteRoute">+ New quote</RouterLink>
                </div>

                <div class="quotes-list">
                  <div v-for="quote in selectedLead.quotes" :key="quote.id" class="quote-item">
                    <span class="quote-number">{{ quote.quote_number }}</span>
                    <span class="status-pill" :class="`status-${quote.status === 'accepted' ? 'won' : 'new'}`">
                      {{ quote.status }}
                    </span>
                    <span class="quote-total">${{ Number(quote.total_amount || 0).toFixed(2) }}</span>
                    <RouterLink
                      v-if="jobForQuote(quote)"
                      class="schedule-job-link scheduled"
                      to="/jobs"
                      title="View job"
                    >
                      <v-icon size="13">mdi-check</v-icon>
                      Scheduled
                    </RouterLink>
                    <button
                      v-else-if="quote.status === 'accepted'"
                      class="schedule-job-link"
                      type="button"
                      @click="openScheduleJob(quote)"
                    >
                      <v-icon size="13">mdi-calendar-plus</v-icon>
                      Schedule job
                    </button>
                  </div>
                  <p v-if="!selectedLead.quotes.length" class="muted-note">
                    No quotes yet
                  </p>
                </div>
              </section>

              <section class="reminders-panel">
                <div class="section-header">
                  <h3>Reminders</h3>
                </div>

                <p v-if="reminderError" class="banner banner-error">{{ reminderError }}</p>

                <div class="reminders-list">
                  <div
                    v-for="reminder in selectedLead.reminders"
                    :key="reminder.id"
                    class="reminder-item"
                    :class="{ overdue: isOverdue(reminder), done: !!reminder.completed_at }"
                  >
                    <input
                      type="checkbox"
                      :checked="!!reminder.completed_at"
                      @change="toggleReminder(reminder)"
                    />
                    <div class="reminder-body">
                      <strong>{{ formatFullTime(reminder.due_at) }}</strong>
                      <span v-if="reminder.note">{{ reminder.note }}</span>
                    </div>
                    <button
                      type="button"
                      class="reminder-delete"
                      title="Delete reminder"
                      @click="deleteReminder(reminder)"
                    >
                      <v-icon size="16">mdi-close</v-icon>
                    </button>
                  </div>
                  <p v-if="!selectedLead.reminders.length" class="muted-note">
                    No reminders set
                  </p>
                </div>

                <div class="reminder-box">
                  <input v-model="reminderDraft.due_at" type="datetime-local" />
                  <input
                    v-model="reminderDraft.note"
                    type="text"
                    placeholder="What to follow up on"
                  />
                  <button
                    class="note-button"
                    type="button"
                    :disabled="!reminderDraft.due_at || reminderSending"
                    title="Add reminder"
                    @click="addReminder"
                  >
                    <v-icon size="19">mdi-plus</v-icon>
                  </button>
                </div>
              </section>
            </aside>
          </div>
        </section>

        <section
          v-else
          class="detail-panel detail-panel-empty"
          aria-label="No lead selected"
        >
          <v-progress-circular v-if="detailLoading" indeterminate size="28" color="primary" />
          <template v-else>
            <v-icon size="34">mdi-inbox-remove-outline</v-icon>
            <h2>No lead selected</h2>
            <p>Try a different search or filter.</p>
          </template>
        </section>
      </div>
    </section>

    <ContactFormModal
      v-model="showContactForm"
      mode="edit"
      :contact="selectedLead?.contact"
      :company-id="company.company_id"
      @saved="onContactSaved"
    />

    <LeadFormModal
      v-model="showLeadForm"
      :company-id="company.company_id"
      :contacts="contacts"
      @saved="onLeadCreated"
    />

    <JobFormModal
      v-model="showJobForm"
      mode="create"
      :company-id="company.company_id"
      :prefill="jobPrefill"
      @saved="onJobScheduled"
    />

    <div v-if="showMessageModal" class="modal-overlay" @click.self="closeMessageModal">
      <div class="modal-card">
        <div class="section-header">
          <h3>Send a message</h3>
          <button class="icon-button" type="button" title="Close" @click="closeMessageModal">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <div class="modal-body">
          <div class="channel-tabs" role="group" aria-label="Message channel">
            <button
              type="button"
              class="channel-tab"
              :class="{ active: messageMode === 'email' }"
              @click="messageMode = 'email'"
            >
              <v-icon size="16">mdi-email-outline</v-icon>
              <span>Email</span>
            </button>
            <button
              v-if="isDevUser"
              type="button"
              class="channel-tab"
              :class="{ active: messageMode === 'text' }"
              @click="messageMode = 'text'"
              title="Texting isn't wired up to a real SMS provider yet — dev-only for now"
            >
              <v-icon size="16">mdi-message-text-outline</v-icon>
              <span>Text</span>
            </button>
          </div>

          <p v-if="messageError" class="banner banner-error">{{ messageError }}</p>

          <p class="modal-recipient">
            To <strong>{{ selectedLead?.contact_name }}</strong>
            — {{ messageMode === 'email' ? (selectedLead?.email || 'no email on file') : (selectedLead?.phone || 'no phone on file') }}
          </p>

          <template v-if="messageMode === 'email'">
            <label class="modal-field">
              <span>Subject</span>
              <input
                v-model="messageDraft.subject"
                type="text"
                :placeholder="`Re: your inquiry${selectedLead?.service ? ' — ' + selectedLead.service : ''}`"
              />
            </label>
            <label class="modal-field">
              <span>Message</span>
              <textarea v-model="messageDraft.body" rows="6" placeholder="Write your email…"></textarea>
            </label>
          </template>

          <template v-else>
            <label class="modal-field">
              <span>Message</span>
              <textarea v-model="messageDraft.body" rows="6" placeholder="Write your text message…"></textarea>
            </label>
            <p class="modal-notice">
              Text messaging isn't connected yet — this will be enabled once an SMS provider is set up.
            </p>
          </template>
        </div>

        <div class="modal-actions">
          <button class="icon-button" type="button" @click="closeMessageModal">Cancel</button>
          <button
            class="send-button"
            type="button"
            :disabled="!messageDraft.body.trim() || messageSending || messageMode === 'text'"
            @click="sendMessage"
          >
            <v-icon size="18">mdi-send</v-icon>
            <span>{{ messageSending ? 'Sending…' : messageMode === 'email' ? 'Send email' : 'Send text' }}</span>
          </button>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped>
.leads-page {
  min-height: calc(100dvh - 70px);
  padding: 28px 18px 104px;
  background: var(--surface-alt);
  color: var(--text-primary);
}

.leads-shell {
  width: min(1440px, 100%);
  margin: 0 auto;
}

.leads-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-end;
  margin-bottom: 18px;
}

.leads-eyebrow {
  margin: 0 0 6px;
  color: #1f7a63;
  font-size: 0.76rem;
  font-weight: 850;
  text-transform: uppercase;
}

.leads-header h1 {
  margin: 0;
  font-size: 2rem;
  line-height: 1.1;
  font-weight: 850;
}

.stats-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(116px, 1fr));
  gap: 10px;
}

.stat-item {
  min-height: 58px;
  display: grid;
  grid-template-columns: auto auto;
  grid-template-rows: auto auto;
  align-items: center;
  column-gap: 8px;
  padding: 10px 12px;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow: 0 10px 24px rgba(23, 35, 31, 0.05);
}

.stat-item .v-icon {
  grid-row: span 2;
  color: var(--text-secondary);
}

.stat-item strong {
  font-size: 1.05rem;
  line-height: 1;
}

.stat-item span {
  color: var(--text-secondary);
  font-size: 0.76rem;
  white-space: nowrap;
}

.banner {
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.86rem;
}

.banner-error {
  color: #8a2b1f;
  background: #fdecea;
  border: 1px solid #f3c6bf;
}

.inbox-layout {
  height: calc(100dvh - 178px);
  min-height: 660px;
  display: grid;
  grid-template-columns: minmax(330px, 380px) minmax(0, 1fr);
  gap: 14px;
}

.inbox-panel,
.conversation-panel,
.form-panel,
.notes-panel {
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow: 0 14px 34px rgba(23, 35, 31, 0.06);
}

.inbox-panel {
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.inbox-toolbar {
  padding: 14px;
  border-bottom: 1px solid var(--border-subtle);
}

.search-field {
  height: 44px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  background: var(--surface-alt);
}

.search-field input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  color: var(--text-primary);
  background: transparent;
  font-size: 0.92rem;
}

.filter-row {
  display: flex;
  gap: 8px;
  padding: 12px 14px;
  overflow-x: auto;
  border-bottom: 1px solid var(--border-subtle);
}

.filter-button {
  height: 34px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  padding: 0 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  background: var(--surface);
  font: inherit;
  font-size: 0.8rem;
  cursor: pointer;
}

.filter-button.active {
  color: #1f5c4d;
  border-color: #9fc8ba;
  background: #eef8f5;
}

.lead-list {
  min-height: 0;
  overflow: auto;
}

.lead-row {
  width: 100%;
  min-height: 116px;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  gap: 11px;
  padding: 14px;
  border: 0;
  border-bottom: 1px solid var(--border-subtle);
  color: var(--text-primary);
  background: var(--surface);
  text-align: left;
  cursor: pointer;
}

/* #f7faf9 is a fixed light hover/selected tint, not theme-reactive — so the
   text inside can't stay on var(--text-primary)/var(--text-secondary)
   either, or it goes near-white on a light background in dark mode. */
.lead-row:hover,
.lead-row.selected {
  background: #f7faf9;
  color: #17231f;
}

.lead-row.selected {
  box-shadow: inset 3px 0 0 #f26e2e;
}

.lead-row:hover .lead-row-top,
.lead-row.selected .lead-row-top {
  color: #17231f;
}

.lead-row:hover .lead-row-top span,
.lead-row.selected .lead-row-top span,
.lead-row:hover .lead-subject,
.lead-row.selected .lead-subject,
.lead-row:hover .lead-meta,
.lead-row.selected .lead-meta {
  color: #56635e;
}

.lead-row:hover .dot,
.lead-row.selected .dot {
  background: #56635e;
}

.lead-avatar,
.detail-avatar {
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 8px;
  background: var(--surface-alt);
  color: var(--text-primary);
  font-weight: 850;
}

.lead-summary {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.lead-row-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  color: var(--text-primary);
}

.lead-row-top strong,
.lead-subject,
.lead-meta span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lead-row-top strong {
  min-width: 0;
  font-size: 0.94rem;
}

.lead-row-top span {
  color: var(--text-secondary);
  font-size: 0.76rem;
}

.lead-subject {
  color: var(--text-secondary);
  font-size: 0.86rem;
}

.lead-meta {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--text-secondary);
  font-size: 0.76rem;
}

.dot {
  width: 4px;
  height: 4px;
  flex: 0 0 auto;
  border-radius: 999px;
  background: var(--text-secondary);
}

.status-pill {
  min-height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  align-self: start;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 750;
  white-space: nowrap;
}

.status-new {
  color: #8a4b0f;
  background: #fff1dc;
}

.status-contacted {
  color: #6a3fa0;
  background: #f2ebfb;
}

.status-quoted {
  color: #29528a;
  background: #e8f1ff;
}

.status-won {
  color: #1f5c4d;
  background: #e6f4ef;
}

.status-lost {
  color: var(--text-secondary);
  background: var(--surface-alt);
}

.empty-list {
  min-height: 180px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.detail-panel {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.detail-panel-empty {
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow: 0 14px 34px rgba(23, 35, 31, 0.06);
  color: var(--text-secondary);
  text-align: center;
}

.detail-panel-empty h2 {
  margin: 4px 0 0;
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 850;
}

.detail-panel-empty p {
  margin: 0;
  font-size: 0.9rem;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  padding: 18px;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-bottom: 0;
  border-radius: 8px 8px 0 0;
}

.detail-title {
  min-width: 0;
  display: flex;
  gap: 12px;
}

.detail-name-row {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.detail-name-row h2 {
  margin: 0;
  font-size: 1.28rem;
  line-height: 1.15;
  font-weight: 850;
}

.detail-title p {
  margin: 5px 0 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.detail-actions {
  display: flex;
  gap: 8px;
}

.channel-button {
  height: 38px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 13px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  background: var(--surface);
  font: inherit;
  font-size: 0.84rem;
  font-weight: 750;
  text-decoration: none;
  cursor: pointer;
}

.channel-button:hover {
  color: #1f5c4d;
  border-color: #9fc8ba;
  background: #f3faf7;
}

.channel-button.danger:hover {
  color: #8a2b1f;
  border-color: #f3c6bf;
  background: #fdecea;
}

.icon-button,
.note-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  background: var(--surface);
  cursor: pointer;
}

.icon-button:hover,
.note-button:hover {
  color: #1f5c4d;
  border-color: #9fc8ba;
  background: #f3faf7;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 0 0 8px 8px;
}

.field-group {
  min-width: 0;
  padding: 14px 16px;
  border-right: 1px solid var(--border-subtle);
}

.field-group:last-child {
  border-right: 0;
}

.field-group span {
  display: block;
  margin-bottom: 5px;
  color: var(--text-secondary);
  font-size: 0.72rem;
  font-weight: 750;
  text-transform: uppercase;
}

.field-group strong {
  display: block;
  min-width: 0;
  overflow: hidden;
  color: var(--text-primary);
  font-size: 0.84rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-body {
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 360px);
  gap: 14px;
  padding: 14px 0 0;
  overflow: auto;
}

.conversation-panel,
.side-panel {
  min-height: 0;
}

.conversation-panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.section-header {
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 14px;
  border-bottom: 1px solid var(--border-subtle);
}

.section-header h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 0.94rem;
  font-weight: 850;
}

.section-header span {
  overflow: hidden;
  color: var(--text-secondary);
  font-size: 0.78rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-thread {
  min-height: 280px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  overflow: auto;
}

.message-bubble {
  width: min(620px, 88%);
  padding: 12px 14px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface);
}

.message-bubble.contractor {
  align-self: flex-end;
  color: #0f332c;
  border-color: #b9dbcf;
  background: #ecf8f4;
}

.message-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
  color: var(--text-secondary);
  font-size: 0.74rem;
}

.message-meta strong {
  color: var(--text-primary);
}

.message-author-group {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.channel-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex: 0 0 auto;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 750;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.channel-email {
  color: #29528a;
  background: #e8f1ff;
}

.channel-sms {
  color: #6a3fa0;
  background: #f2ebfb;
}

.message-subject {
  margin: 0 0 4px;
  color: var(--text-secondary);
  font-size: 0.78rem;
  font-style: italic;
}

.message-bubble .message-body {
  margin: 0;
  color: var(--text-primary);
  font-size: 0.92rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

textarea {
  width: 100%;
  min-width: 0;
  resize: vertical;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 11px 12px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: inherit;
  font-size: 0.9rem;
  line-height: 1.45;
  outline: none;
}

textarea:focus,
.search-field:focus-within {
  border-color: #9fc8ba;
  box-shadow: 0 0 0 3px rgba(31, 122, 99, 0.1);
}

.send-button {
  min-width: 130px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  align-self: end;
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  background: #f26e2e;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 800;
  cursor: pointer;
}

.send-button:disabled,
.note-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.side-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-panel,
.notes-panel {
  overflow: hidden;
}

.submission-list {
  margin: 0;
}

.submission-list div {
  padding: 12px 14px;
  border-bottom: 1px solid var(--border-subtle);
}

.submission-list div:last-child {
  border-bottom: 0;
}

.submission-list dt {
  margin-bottom: 5px;
  color: var(--text-secondary);
  font-size: 0.72rem;
  font-weight: 750;
  text-transform: uppercase;
}

.submission-list dd {
  margin: 0;
  color: var(--text-primary);
  font-size: 0.88rem;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.notes-list {
  display: grid;
  gap: 9px;
  padding: 14px;
}

.note-item {
  margin: 0;
  padding: 10px 11px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font-size: 0.86rem;
  line-height: 1.45;
}

.muted-note {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.88rem;
}

.note-box {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 9px;
  padding: 0 14px 14px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 14px;
}

.new-contact-button {
  height: 38px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  background: #1f7a63;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
}

.view-toggle {
  display: inline-flex;
  flex: 0 0 auto;
  padding: 3px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface);
}

.view-toggle-button {
  height: 32px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 11px;
  border: 0;
  border-radius: 6px;
  color: var(--text-secondary);
  background: transparent;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
}

.view-toggle-button.active {
  color: #1f5c4d;
  background: #eef8f5;
}

.board-layout {
  height: calc(100dvh - 178px);
  min-height: 520px;
}

.row-time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.repeat-tag {
  color: #b45309;
  font-weight: 750;
}

.repeat-pill {
  color: #b45309;
  background: #fff4e0;
}

.composer-row {
  display: grid;
  gap: 10px;
  padding: 14px;
  border-top: 1px solid var(--border-subtle);
  background: var(--surface);
}

.composer-row .note-box {
  padding: 0;
  border-top: 0;
}

.timeline-note {
  width: min(620px, 88%);
  padding: 12px 14px;
  border: 1px solid #f0e2b8;
  border-radius: 8px;
  background: #fffaef;
}

.timeline-note-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
  color: #8a6d1f;
  font-size: 0.72rem;
  font-weight: 750;
  text-transform: uppercase;
}

.timeline-note .message-body {
  margin: 0 0 6px;
  color: #4a3f1f;
  font-size: 0.9rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.timeline-status {
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: center;
  padding: 6px 12px;
  color: var(--text-secondary);
  font-size: 0.82rem;
}

.timeline-status .v-icon {
  color: var(--text-secondary);
}

.timeline-status-time {
  color: var(--text-secondary);
  font-size: 0.74rem;
}

.new-quote-link {
  color: #1f7a63;
  font-size: 0.8rem;
  font-weight: 750;
  text-decoration: none;
}

.new-quote-link:hover {
  text-decoration: underline;
}

.quotes-list {
  display: grid;
  gap: 8px;
  padding: 14px;
}

.quote-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 8px;
  padding: 10px 11px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
  font-size: 0.86rem;
}

.quote-number {
  overflow: hidden;
  color: var(--text-primary);
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quote-total {
  color: var(--text-primary);
  font-weight: 700;
}

.schedule-job-link {
  grid-column: 1 / -1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 6px 10px;
  border: 1px solid #9fc8ba;
  border-radius: 8px;
  color: #1f5c4d;
  background: #f3faf7;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 750;
  text-decoration: none;
  cursor: pointer;
}

.schedule-job-link:hover {
  background: #e6f4ef;
}

.schedule-job-link.scheduled {
  border-color: var(--border-subtle);
  color: var(--text-secondary);
  background: var(--surface-alt);
}

.reminders-list {
  display: grid;
  gap: 8px;
  padding: 14px;
}

.reminder-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 9px;
  padding: 10px 11px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
}

.reminder-item.overdue {
  border-color: #f3c6bf;
  background: #fdecea;
}

.reminder-item.done {
  opacity: 0.55;
}

.reminder-body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.reminder-body strong {
  font-size: 0.84rem;
}

.reminder-body span {
  overflow: hidden;
  color: var(--text-secondary);
  font-size: 0.8rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reminder-delete {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  color: var(--text-secondary);
  background: transparent;
  cursor: pointer;
}

.reminder-delete:hover {
  color: #8a2b1f;
  background: #fdecea;
}

.reminder-box {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 9px;
  padding: 0 14px 14px;
}

.reminder-box input[type='text'],
.reminder-box input[type='datetime-local'] {
  min-width: 0;
  height: 38px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 0 10px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: inherit;
  font-size: 0.84rem;
  outline: none;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(23, 35, 31, 0.45);
}

.modal-card {
  width: min(440px, 100%);
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 10px;
  background: var(--surface);
  box-shadow: 0 24px 60px rgba(23, 35, 31, 0.25);
}

.modal-body {
  display: grid;
  gap: 12px;
  padding: 16px;
  overflow: auto;
}

.modal-field {
  display: grid;
  gap: 5px;
}

.modal-field span {
  color: var(--text-secondary);
  font-size: 0.76rem;
  font-weight: 750;
  text-transform: uppercase;
}

.modal-field input,
.modal-field select {
  height: 40px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 0 12px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: inherit;
  font-size: 0.9rem;
  outline: none;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 16px;
  border-top: 1px solid var(--border-subtle);
}

.channel-tabs {
  display: inline-flex;
  align-self: start;
  padding: 3px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
}

.channel-tab {
  height: 32px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  border: 0;
  border-radius: 6px;
  color: var(--text-secondary);
  background: transparent;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
}

.channel-tab.active {
  color: #1f5c4d;
  background: #eef8f5;
}

.modal-recipient {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.86rem;
}

.modal-recipient strong {
  color: var(--text-primary);
}

.modal-notice {
  margin: 0;
  padding: 10px 12px;
  border: 1px solid #f0e2b8;
  border-radius: 8px;
  color: #8a6d1f;
  background: #fffaef;
  font-size: 0.82rem;
  line-height: 1.4;
}

@media (max-width: 1180px) {
  .detail-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .field-group:nth-child(3n) {
    border-right: 0;
  }

  .detail-body {
    grid-template-columns: 1fr;
  }

  .side-panel {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .leads-page {
    padding: 20px 12px 94px;
  }

  .leads-header {
    align-items: stretch;
    flex-direction: column;
  }

  .header-right {
    flex-direction: column;
    align-items: stretch;
  }

  .stats-strip {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .new-contact-button {
    justify-content: center;
  }

  .inbox-layout {
    height: auto;
    min-height: 0;
    grid-template-columns: 1fr;
  }

  .inbox-panel,
  .detail-panel {
    max-height: none;
  }

  .lead-list {
    max-height: 420px;
  }
}

@media (max-width: 700px) {
  .leads-header h1 {
    font-size: 1.6rem;
  }

  .stats-strip,
  .side-panel,
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .stat-item {
    grid-template-columns: auto auto 1fr;
    grid-template-rows: auto;
  }

  .stat-item .v-icon {
    grid-row: auto;
  }

  .detail-header {
    flex-direction: column;
  }

  .detail-actions {
    justify-content: flex-start;
  }

  .field-group,
  .field-group:nth-child(3n) {
    border-right: 0;
  }

  .lead-row {
    grid-template-columns: 38px minmax(0, 1fr);
  }

  .lead-row > .status-pill {
    grid-column: 2;
    align-self: start;
    justify-self: start;
  }

  .note-box {
    grid-template-columns: 1fr;
  }

  .send-button {
    width: 100%;
  }
}
</style>
